import { Path, classRegistry, type TSVGReviver } from 'fabric';
import draw from 'draw-svg-path';
import { svgPathProperties } from 'svg-path-properties';

// firefox doesn't support SVG getTotalLength() or getPointAtLength()
// so we use this library to replace these functions
// and user agent sniffing to determine which one to use

const isFirefox = false;

const waveLength = 6;
const amplitude = 3;
let slavePathEl: SVGPathElement | undefined;

// Offscreen, never-rendered svg+path used only as a slave to measure path lengths with.
if (typeof window !== 'undefined') {
  const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  slavePathEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'path',
  ) as SVGPathElement;
  svgEl.append(slavePathEl);
}

// Adapted from: https://stackoverflow.com/questions/42441472/draw-a-squiggly-line-in-svg
const makeSquiggle = (stringifiedSvgPath: string): string => {
  let fakeSlavePathEl = {} as ReturnType<typeof svgPathProperties>;
  let pathLen = 0;
  if (isFirefox) {
    // firefox uses the library implementation.
    fakeSlavePathEl = svgPathProperties(stringifiedSvgPath);
    pathLen = fakeSlavePathEl.getTotalLength();
  } else {
    // other browsers use the native implementation: set 'd' on the slave path so native getTotalLength() can measure it.
    slavePathEl!.setAttribute('d', stringifiedSvgPath);
    pathLen = slavePathEl!.getTotalLength();
  }

  // Adjust step so that there are a whole number of steps along the path
  let pos = isFirefox
    ? fakeSlavePathEl.getPointAtLength(0)
    : slavePathEl!.getPointAtLength(0);
  let newPath = 'M' + [pos.x, pos.y].join(','); // M5,5
  let side = -1;
  const numSteps = Math.round(pathLen / waveLength);

  for (let i = 1; i <= numSteps; i++) {
    const last = pos;
    pos = isFirefox
      ? fakeSlavePathEl.getPointAtLength((i * pathLen) / numSteps)
      : slavePathEl!.getPointAtLength((i * pathLen) / numSteps);

    // Find a point halfway between last and pos. Then find the point that is
    // perpendicular to that line segment, and is amplitude away from
    // it on the side of the line designated by 'side' (-1 or +1).
    // This point will be the control point of the quadratic curve forming the
    // squiggle step.

    // The vector from the last point to this one
    const vector = {
      x: pos.x - last.x,
      y: pos.y - last.y,
    };

    // The length of this vector
    // eslint-disable-next-line unicorn/prefer-modern-math-apis -- keep sqrt(x²+y²) exact; Math.hypot can differ in the last ULP and this length feeds the squiggle SVG geometry (pixel-critical)
    const vectorLen = Math.sqrt(vector.x * vector.x + vector.y * vector.y);

    // The point halfway between last point and this one
    const half = {
      x: last.x + vector.x / 2,
      y: last.y + vector.y / 2,
    };

    // The vector that is perpendicular to 'vector'
    const perpVector = {
      x: -((amplitude * vector.y) / vectorLen),
      y: (amplitude * vector.x) / vectorLen,
    };

    // Now calculate the control point position
    const controlPoint = {
      x: half.x + perpVector.x * side,
      y: half.y + perpVector.y * side,
    };
    newPath += 'Q' + [controlPoint.x, controlPoint.y, pos.x, pos.y].join(',');

    // Switch the side (for next step)
    side = -side;
  }
  return newPath;
};

// Custom Path subclass for the "squiggley" line type. Registered on the classRegistry so serialized
// 'SquiggleyLine' objects can be enlivened (import strips + rebuilds them from control points, so this
// is defensive).
class SquiggleyLine extends Path {
  static type = 'SquiggleyLine';

  constructor(
    path: ConstructorParameters<typeof Path>[0],
    options?: ConstructorParameters<typeof Path>[1],
  ) {
    super(path, options);
    this.options = options as Record<string, unknown> | undefined;
  }

  toSVG(reviver?: TSVGReviver): string {
    let stringifiedSvgPath = this.path.toString();

    stringifiedSvgPath = stringifiedSvgPath
      .replaceAll('M,', 'M')
      .replaceAll('Q,', 'Q');

    const squiggledSvgPath = makeSquiggle(stringifiedSvgPath);

    const markup = super.toSVG(reviver);
    const fabricLine = document.createElement('div');
    fabricLine.innerHTML = markup;
    fabricLine.querySelector('path')?.setAttribute('d', squiggledSvgPath);
    return fabricLine.innerHTML;
  }

  // eslint-disable-next-line unicorn/prefer-private-class-fields -- `_render` is a Fabric override hook called by name; it cannot be a private field
  _render(ctx: CanvasRenderingContext2D): void {
    let stringifiedSvgPath = this.path.toString();

    stringifiedSvgPath = stringifiedSvgPath
      .replaceAll('M,', 'M')
      .replaceAll('Q,', 'Q');

    const squiggledSvgPath = makeSquiggle(stringifiedSvgPath);

    ctx.lineWidth = this.options?.strokeWidth as number;
    ctx.strokeStyle = this.options?.stroke as string;
    ctx.beginPath();
    draw(ctx, squiggledSvgPath); // replaces new Path2D
    ctx.stroke();
    ctx.closePath();

    // super.render(ctx) // this does the normal line. we don't want that
  }
}

/* eslint-disable unicorn/no-top-level-side-effects -- registering the custom Fabric class + SVG class is the purpose of this module (required for (de)serialisation) */
classRegistry.setClass(SquiggleyLine, 'SquiggleyLine');
classRegistry.setSVGClass(SquiggleyLine, 'SquiggleyLine');
/* eslint-enable unicorn/no-top-level-side-effects */

export default SquiggleyLine;
